import React from 'react';
import { AnalyticsSummary } from '../../../types/analytics';

interface AnalyticsSummaryWidgetProps {
  summary: AnalyticsSummary | null;
  loading: boolean;
  activePlatformFilter?: 'ALL' | 'FACEBOOK' | 'INSTAGRAM' | 'INSTAGRAM_REELS' | 'LINKEDIN' | 'YOUTUBE' | 'YOUTUBE_SHORTS';
}

const AnalyticsSummaryWidget: React.FC<AnalyticsSummaryWidgetProps> = ({
  summary,
  loading,
  activePlatformFilter = 'ALL',
}) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-6 mb-6">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="animate-pulse rounded-lg border border-theme-border bg-theme-surface p-4 shadow-sm"
          >
            <div className="h-4 w-1/2 rounded bg-theme-border/50 mb-4"></div>
            <div className="h-8 w-3/4 rounded bg-theme-border/50"></div>
          </div>
        ))}
      </div>
    );
  }

  if (!summary) {
    return null;
  }

  // Mirror PostAnalyticsTable column rules: video views don't apply to
  // Facebook/LinkedIn; impressions and reach don't apply to YouTube.
  const hideVideoViews =
    activePlatformFilter === 'FACEBOOK' || activePlatformFilter === 'LINKEDIN' || activePlatformFilter === 'INSTAGRAM';
  const hideImpressionsAndReach =
    activePlatformFilter === 'YOUTUBE' || activePlatformFilter === 'YOUTUBE_SHORTS';

  const stats = [
    {
      label: 'Total Posts',
      value: summary.post_count || 0,
      icon: 'dynamic_feed',
      gradient: 'from-blue-500/10 to-indigo-500/10',
      iconColor: 'text-blue-500',
      hidden: false,
      description: 'The total number of posts analyzed.',
    },
    {
      label: 'Total Impressions',
      value: summary.totals?.impressions || 0,
      icon: 'visibility',
      gradient: 'from-purple-500/10 to-indigo-500/10',
      iconColor: 'text-purple-500',
      hidden: hideImpressionsAndReach,
      description: 'The total number of times your content was displayed on screen.',
    },
    {
      label: 'Total Reach',
      value: summary.totals?.reach || 0,
      icon: 'public',
      gradient: 'from-emerald-500/10 to-teal-500/10',
      iconColor: 'text-emerald-500',
      hidden: hideImpressionsAndReach,
      description: 'The number of unique users who saw your content.',
    },
    {
      label: 'Total Engagement',
      value: summary.totals?.engagement || 0,
      icon: 'thumb_up',
      gradient: 'from-orange-500/10 to-red-500/10',
      iconColor: 'text-orange-500',
      hidden: false,
      description: 'The total number of interactions (likes, comments, shares, etc.) with your content.',
    },
    {
      label: 'Total Shares',
      value: summary.totals?.shares || 0,
      icon: 'share',
      gradient: 'from-pink-500/10 to-rose-500/10',
      iconColor: 'text-pink-500',
      hidden: false,
      description: 'The total number of times your content was shared.',
    },
    {
      label: 'Total Video Views',
      value: summary.totals?.video_views || 0,
      icon: 'smart_display',
      gradient: 'from-red-500/10 to-orange-500/10',
      iconColor: 'text-red-500',
      hidden: hideVideoViews,
      description: 'The total number of times your videos were viewed.',
    },
  ];

  const visibleStats = stats.filter((stat) => !stat.hidden);
  const gridColsClass =
    visibleStats.length <= 4 ? 'md:grid-cols-4' : visibleStats.length === 5 ? 'md:grid-cols-5' : 'md:grid-cols-6';

  return (
    <div className="mb-6">
      <div className={`grid grid-cols-1 gap-4 ${gridColsClass}`}>
        {visibleStats.map((stat, idx) => (
          <div
            key={idx}
            title={stat.description}
            className={`relative overflow-hidden rounded-xl border border-theme-border bg-theme-surface p-5 shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5 cursor-help`}
          >
            {/* Decorative Gradient Background */}
            <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-50 dark:opacity-15`}></div>
            
            <div className="relative flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <span className={`material-icons ${stat.iconColor} text-2xl`}>
                  {stat.icon}
                </span>
                <div className="h-2 w-8 rounded-full bg-theme-border/20"></div>
              </div>
              
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-theme-fg-muted dark:text-theme-fg">
                  {stat.label}
                </p>
                <h4 className="mt-1 text-2xl font-bold text-theme-fg">
                  {stat.value.toLocaleString()}
                </h4>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AnalyticsSummaryWidget;
